import { createTheme } from '@mui/material';

const brand = {
  main: '#63ace5',
  light: '#8bc4ed',
  dark: '#4a90c2',
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brand.main,
      light: brand.light,
      dark: brand.dark,
      contrastText: '#0f1419',
    },
    secondary: {
      main: '#5b7fa3',
      light: '#7c9ab8',
      dark: '#3f5c78',
      contrastText: '#f8fafc',
    },
    error: { main: '#f87171' },
    warning: { main: '#fbbf24' },
    success: { main: '#4ade80' },
    divider: 'rgba(248, 250, 252, 0.1)',
    background: {
      default: '#0f1419',
      paper: '#1a2229',
      secondary: '#243038',
      modal: '#1e2730',
      messageBox: '#1e2730',
      received: '#2a4d69',
      selected: 'rgba(99, 172, 229, 0.35)',
      hover: 'rgba(255, 255, 255, 0.06)',
      internal: 'rgba(255, 243, 120, 0.2)',
      tabIndicator: '#f8fafc',
      textField: '#1e2730',
      icon: '#cbd5e1',
      toast: '#e2e8f0',
      chatHeader: '#1a242c',
      divider: 'rgba(248, 250, 252, 0.1)',
      card: '#1a2229',
    },
    quickAccess: {
      tabInactiveBg: 'rgba(255, 255, 255, 0.12)',
      tabInactiveText: 'rgba(255, 255, 255, 0.72)',
      tabInactiveBorder: 'rgba(255, 255, 255, 0.2)',
      tabInactiveHoverBg: 'rgba(255, 255, 255, 0.2)',
      tabActiveBg: 'rgba(255, 255, 255, 0.22)',
      tabActiveText: 'rgba(255, 255, 255, 0.98)',
      tabActiveBorder: 'rgba(255, 255, 255, 0.32)',
      tabActiveAccent: 'rgba(126, 200, 245, 0.7)',
      tabIcon: 'rgba(255, 255, 255, 0.85)',
    },
    /*
     * CONTRASTE MEDIDO, nao escolhido a olho (Gisele, 23/10/2026).
     *
     * "No modo escuro, revisar o contraste dos textos, pois algumas
     * informacoes em cinza claro ficam muito apagadas e dificultam a
     * leitura. Seria interessante revisar esse padrao em todo o AloDesk."
     *
     * Medido antes de mexer: `secondary` (#94a3b8) ja passava o AA do WCAG
     * em todos os fundos (5.27 no pior deles). Quem reprovava era
     * `caption`/`disabled` (#64748b): 2.84 a 3.89 -- abaixo de 4.5 em TODO
     * fundo, e abaixo ate do 3:1 de texto grande no mais claro deles.
     *
     * E sao essas as cores que a tela usa para legenda e horario, que e
     * exatamente a "informacao apagada" do relato.
     *
     * As duas subiram um degrau, mantendo os tres niveis distinguiveis e
     * a mesma familia de cinza-azulado. `darkTheme.contraste.test.js` mede
     * cada cor contra cada fundo e reprova o que ficar abaixo do AA -- sem
     * ele, a proxima cor escolhida a olho repete o problema, e quem escolhe
     * quase nunca esta no monitor onde a reclamacao nasceu.
     */
    text: {
      primary: '#f1f5f9',
      secondary: '#b0bdcc',
      caption: '#94a3b8',
      disabled: '#94a3b8',
      textField: '#f1f5f9',
      button: '#0f1419',
      buttonCaption: '#f8fafc',
      tabSelected: '#ffffff',
      tabUnselected: '#94a3b8',
    },
    border: {
      primary: 'rgba(248, 250, 252, 0.12)',
      secondary: 'rgba(148, 163, 184, 0.35)',
    },
    button: {
      primary: brand.main,
      secondary: '#5b7fa3',
      disabled: '#475569',
    },
    switch: {
      on: brand.main,
      off: '#64748b',
      track: '#334155',
      trackChecked: '#4a6d82',
    },
    noticeBoard: {
      listSelectedBg: 'rgba(99, 172, 229, 0.2)',
      listSelectedBgHover: 'rgba(99, 172, 229, 0.3)',
      listSelectedBorder: '#63ace5',
    },
    action: {
      hover: 'rgba(255, 255, 255, 0.06)',
      selected: 'rgba(99, 172, 229, 0.15)',
      disabled: 'rgba(255, 255, 255, 0.08)',
      disabledBackground: 'rgba(255, 255, 255, 0.08)',
    },
  },
  typography: {
    fontFamily: '"DM Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h4: { fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
    caption: { color: 'inherit' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { letterSpacing: '0.01em' },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 18px',
          minHeight: 40,
        },
        sizeSmall: {
          padding: '6px 14px',
          minHeight: 34,
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '10px 22px',
          minHeight: 46,
          fontSize: '1rem',
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }),
        containedSecondary: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },
        }),
        outlined: ({ theme }) => ({
          borderWidth: '1.5px',
          borderColor: theme.palette.border.primary,
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            borderColor: theme.palette.primary.light,
            backgroundColor: 'rgba(99, 172, 229, 0.08)',
          },
        }),
        text: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
        textPrimary: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderRadius: 10,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.secondary,
          '& .left-sidebar': {
            backgroundColor: 'var(--sidebar-shell-bg)',
            '& .MuiSvgIcon-root': {
              color: '#fff',
            },
            '& .MuiTypography-root.MuiTypography-caption': {
              color: '#fff',
            },
          },
          '& .header': {
            backgroundColor: 'var(--sidebar-shell-bg)',
            color: 'var(--white-900)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            height: '10vh',
          },
          '& .header .MuiIconButton-root': {
            color: 'rgba(255,255,255,0.92)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.12)',
            },
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.background.icon,
          },
          // Ícone dentro de botão segue a cor do botão. O motivo e a medição
          // estão no `lightTheme`, onde o defeito aparece mais forte; a regra
          // fica nos dois para os temas não divergirem.
          '& .MuiButton-root .MuiSvgIcon-root': {
            color: 'inherit',
          },
          '& .profileSection': {
            border: `1px solid ${theme.palette.border.primary}`,
            width: '88vw',
            padding: '20px',
            marginTop: '20px',
            borderRadius: 12,
          },
          '& .separator': {
            width: '88vw',
            height: '1px',
            background: theme.palette.border.primary,
            marginTop: '20px',
          },
          '& .tabContent': {
            border: `1px solid ${theme.palette.border.primary}`,
            width: '88vw',
            padding: '20px',
            marginTop: '20px',
            marginLeft: '20px',
            borderRadius: 12,
          },
          '.MuiDataGrid-columnHeaderTitle': {
            color: 'var(--table-header)',
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          color: theme.palette.text.primary,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.border.secondary,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.secondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1.5px',
          },
        }),
        input: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          color: theme.palette.text.primary,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '& .MuiInputBase-input': {
            backgroundColor: theme.palette.background.textField,
          },
        }),
        iconOutlined: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&.Mui-focused': { color: theme.palette.primary.main },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .Mui-focused fieldset': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '& .MuiInputBase-root': {
            backgroundColor: theme.palette.background.textField,
            borderRadius: 10,
          },
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          borderRadius: 10,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper || theme.palette.background.default,
          color: theme.palette.text.primary,
          borderRadius: 12,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 14,
          border: `1px solid ${theme.palette.border.primary}`,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          border: `1px solid ${theme.palette.border.primary}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            opacity: 1,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
        },
      },
    },
  },
});

export default darkTheme;
